import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Body } from "@hypercard/web-components";
import { Label } from "@/components/ui/label";
import {
  ExpenseReportAnalyticGroup,
  StipendExpenseAnalyticGroup,
  useAdminCustomFieldsQuery,
} from "@common/utils/graphql";
import { useMemo } from "react";
import {
  getNameForExpenseReportAnalyticGroup,
  getNameForStipendExpenseAnalyticGroup,
  NAME_FOR_EXPENSE_REPORT_ANALYTIC_GROUP,
  NAME_FOR_STIPEND_EXPENSE_ANALYTIC_GROUP,
} from "@domains/stats/utils";

// Types
type ReportProps = {
  queryType: "report";
  value: {
    groupedBy: ExpenseReportAnalyticGroup;
    groupByCustomFieldEntityId: number | null;
  };
  onChange: ({
    groupedBy,
    groupByCustomFieldEntityId,
  }: {
    groupedBy: ExpenseReportAnalyticGroup;
    groupByCustomFieldEntityId: number | null;
  }) => void;
};

type StipendProps = {
  queryType: "stipend";
  value: {
    groupedBy: StipendExpenseAnalyticGroup;
    groupByCustomFieldEntityId: number | null;
  };
  onChange: ({
    groupedBy,
    groupByCustomFieldEntityId,
  }: {
    groupedBy: StipendExpenseAnalyticGroup;
    groupByCustomFieldEntityId: number | null;
  }) => void;
};

type Props = {
  showLabel?: boolean;
} & (ReportProps | StipendProps);

// Component
export const GroupBySelect = ({
  queryType,
  showLabel = true,
  value,
  onChange,
}: Props) => {
  const { data: customFieldData } = useAdminCustomFieldsQuery({
    fetchPolicy: "cache-first",
  });

  const customFields =
    customFieldData?.adminCustomFields.__typename ===
    "QueryAdminCustomFieldsSuccess"
      ? customFieldData.adminCustomFields.data.customFields
      : [];
  const options = useMemo(
    () =>
      queryType === "report"
        ? NAME_FOR_EXPENSE_REPORT_ANALYTIC_GROUP
        : NAME_FOR_STIPEND_EXPENSE_ANALYTIC_GROUP,
    [queryType],
  );

  const selectedCustomField = customFields.find(
    (field) => field.id == value.groupByCustomFieldEntityId,
  );
  const displayName = useMemo(
    () =>
      queryType === "report"
        ? getNameForExpenseReportAnalyticGroup(
            value.groupedBy,
            selectedCustomField,
          )
        : getNameForStipendExpenseAnalyticGroup(value.groupedBy),
    [queryType, value, selectedCustomField],
  );

  const handleReportValueChange = (
    val: string | ExpenseReportAnalyticGroup,
  ) => {
    if (queryType === "report") {
      if (
        NAME_FOR_EXPENSE_REPORT_ANALYTIC_GROUP[
          val as ExpenseReportAnalyticGroup
        ]
      ) {
        onChange({
          groupedBy: val as ExpenseReportAnalyticGroup,
          groupByCustomFieldEntityId: null,
        });
      } else {
        onChange({
          groupedBy: ExpenseReportAnalyticGroup.CustomField,
          groupByCustomFieldEntityId: Number(val),
        });
      }
    }
  };

  const handleStipendValueChange = (val: string) => {
    if (queryType === "stipend") {
      onChange({
        groupedBy: val as StipendExpenseAnalyticGroup,
        groupByCustomFieldEntityId: null,
      });
    }
  };

  const handleValueChange =
    queryType === "report" ? handleReportValueChange : handleStipendValueChange;
  return (
    <div className="space-y-2">
      {showLabel && <Label>Group By</Label>}
      <Select
        value={String(value.groupByCustomFieldEntityId) ?? value.groupedBy}
        onValueChange={handleValueChange}
      >
        <SelectTrigger className="min-w-[200px]">
          <Body size="sm" weight="medium">
            {displayName}
          </Body>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {Object.entries(options)
              .filter(([key]) => key !== ExpenseReportAnalyticGroup.CustomField)
              .map(([key, name]) => (
                <SelectItem key={key} value={key}>
                  <Body size="sm" weight="regular">
                    {name}
                  </Body>
                </SelectItem>
              ))}
            {queryType === "report" &&
              customFields.map((field) => (
                <SelectItem key={field.id} value={`${field.id}`}>
                  <Body size="sm" weight="regular">
                    {field.name}
                  </Body>
                </SelectItem>
              ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};
